import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GOAL_TYPES,
  goalCombinationError,
  normalizeGoalTypes,
  returnsToStart,
  type GoalType,
} from '@flugwettrennen/shared';
import { api, ApiError } from '../api';
import { AirportPicker } from '../components/AirportPicker';
import {
  DEFAULT_OPTION_VALUES,
  RoundOptionsFields,
  type RoundOptionValues,
} from '../components/RoundOptionsFields';

function defaultStart(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  // datetime-local erwartet lokale Zeit ohne Zone
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

export function CreateRoundPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [goalTypes, setGoalTypes] = useState<GoalType[]>(['world_east']);
  const [start, setStart] = useState('');
  const [dest, setDest] = useState('');
  const [sameAsStart, setSameAsStart] = useState(true);
  const [startDateTime, setStartDateTime] = useState(defaultStart());
  const [mode, setMode] = useState<'live' | 'sim'>('sim');

  const [options, setOptions] = useState<RoundOptionValues>(DEFAULT_OPTION_VALUES);
  const patchOptions = (patch: Partial<RoundOptionValues>) =>
    setOptions((o) => ({ ...o, ...patch }));

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isFree = goalTypes.includes('free');
  // Führt ein Ziel zum Start zurück, endet die Runde dort – ein eigenes Ziel wäre bedeutungslos.
  const endsAtStart = returnsToStart(goalTypes);
  const effectiveDest = endsAtStart || sameAsStart ? start : dest;

  /** Warum sich dieses Ziel gerade nicht dazunehmen lässt (null = es geht). */
  function blockReason(g: GoalType): string | null {
    if (goalTypes.includes(g)) return null;
    return goalCombinationError([...goalTypes, g]);
  }

  function toggleGoal(g: GoalType, on: boolean) {
    setGoalTypes((cur) => {
      const next = on ? normalizeGoalTypes([...cur, g]) : cur.filter((x) => x !== g);
      // Mindestens ein Ziel bleibt stehen – eine Runde ohne Ziel gibt es nicht.
      return next.length > 0 ? next : cur;
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!start || !effectiveDest) {
      setError(t('errors.generic'));
      return;
    }
    setBusy(true);
    try {
      const res = await api.rounds.create({
        name,
        goalTypes,
        startAirportIata: start,
        destAirportIata: effectiveDest,
        startDateTime: new Date(startDateTime).toISOString(),
        mode,
        options: {
          ...options,
          customGoalText: isFree ? options.customGoalText || null : null,
        },
      });
      navigate(`/round/${res.round.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? t([`errors.${err.code}`, 'errors.generic']) : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container page">
      <div className="eyebrow">{t('nav.newRound')}</div>
      <h1>{t('create.title')}</h1>
      <p className="muted" style={{ marginTop: -4 }}>{t('create.subtitle')}</p>

      <form className="grid-2" onSubmit={submit} style={{ alignItems: 'start', marginTop: 12 }}>
        <div className="card stack">
          <div className="field">
            <label htmlFor="nm">{t('create.name')}</label>
            <input id="nm" value={name} placeholder={t('create.namePlaceholder')} onChange={(e) => setName(e.target.value)} required data-testid="round-name" />
          </div>

          <fieldset className="field goal-picker" data-testid="goal-types">
            <legend>{t('create.goals')}</legend>
            <span className="hint">{t('create.goalsHint')}</span>
            {GOAL_TYPES.map((g) => {
              const reason = blockReason(g);
              return (
                <label key={g} className={`goal-option ${reason ? 'blocked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={goalTypes.includes(g)}
                    disabled={reason !== null}
                    onChange={(e) => toggleGoal(g, e.target.checked)}
                    data-testid={`goal-${g}`}
                  />
                  <span>
                    <strong>{t(`goals.${g}`)}</strong>
                    <span className="hint">{reason ? t(`goalCombo.${reason}`) : t(`goals.desc.${g}`)}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          <AirportPicker label={t('create.startAirport')} value={start} onChange={setStart} testId="start-airport" />

          {endsAtStart ? (
            <span className="hint" data-testid="ends-at-start">{t('create.endsAtStart')}</span>
          ) : (
            <>
              <label className="row" style={{ gap: 8 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={sameAsStart} onChange={(e) => setSameAsStart(e.target.checked)} data-testid="dest-same-as-start" />
                <span>{t('create.sameAsStart')}</span>
              </label>
              {!sameAsStart && (
                <AirportPicker label={t('create.destAirport')} value={dest} onChange={setDest} testId="dest-airport" />
              )}
            </>
          )}

          <div className="field">
            <label htmlFor="dt">{t('create.startDateTime')}</label>
            <input id="dt" type="datetime-local" value={startDateTime} onChange={(e) => setStartDateTime(e.target.value)} required />
          </div>

          <div className="field">
            <label>{t('create.mode')}</label>
            <div className="tabs">
              <button type="button" className={mode === 'sim' ? 'active' : ''} onClick={() => setMode('sim')} data-testid="mode-sim">
                {t('create.modeSim')}
              </button>
              <button type="button" className={mode === 'live' ? 'active' : ''} onClick={() => setMode('live')} data-testid="mode-live">
                {t('create.modeLive')}
              </button>
            </div>
            <span className="hint">{mode === 'sim' ? t('create.modeSimHint') : t('create.modeLiveHint')}</span>
          </div>
        </div>

        <div className="card stack">
          <div className="eyebrow">{t('create.options')}</div>

          <RoundOptionsFields values={options} onChange={patchOptions} goalTypes={goalTypes} />

          {error && <div className="notice notice-error">{error}</div>}

          <button className="btn btn-go" type="submit" disabled={busy} data-testid="create-submit">
            {busy ? t('common.loading') : t('create.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
